import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ChevronLeft, Loader2, Video, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const isod = (d) => d.toISOString().slice(0, 10);
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// Meetings the user BOOKED for others (and Host meetings they may manage). Reschedule/cancel use the same
// shared server service as Candidate self-service; the Meeting record is updated in place.
export default function MeetingsIBooked() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [rx, setRx] = useState(null);
  const [cx, setCx] = useState(null);
  const load = () => api.get("/booking/meetings").then((r) => setItems(r.data.items)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = sp.get("reschedule");
    if (id && items) { const m = items.find((x) => x.id === id && x.can_reschedule); if (m) setRx(m); }
  }, [items, sp]);

  const cancel = async () => {
    try { await api.post(`/booking/meetings/${cx.id}/cancel`); toast.success("Appointment cancelled"); setCx(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (items === null) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-3" data-testid="meetings-booked">
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500" data-testid="meetings-booked-empty">
          <CalendarClock className="w-8 h-8 mx-auto text-slate-300 mb-2" />No meetings booked for others yet.
          <div className="mt-3"><Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={() => navigate("/meetings/book")}>Book a Meeting</Button></div>
        </div>
      ) : items.map((m) => (
        <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`booked-${m.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-800">{m.candidate_name || "Attendee"} · {m.appointment_type}</p>
              <Badge variant="outline" className={m.status === "cancelled" ? "text-rose-600 border-rose-200" : "text-emerald-700 border-emerald-200"} data-testid={`booked-status-${m.id}`}>{m.status}</Badge>
            </div>
            <p className="text-sm text-slate-600 mt-0.5">{fmt(m.start)} · Host: <b>{m.host_name}</b></p>
            <p className="text-xs text-slate-400 mt-0.5">Originally booked by {m.booked_by_name || "—"}{m.last_modified_by_name ? ` · Last modified by ${m.last_modified_by_name}` : ""}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {m.meeting_link && m.status === "scheduled" && <a href={m.meeting_link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Video className="w-3.5 h-3.5 mr-1" />Join</Button></a>}
            {m.can_view_candidate && m.person_id && <Button size="sm" variant="outline" onClick={() => navigate(`/people/detail/${m.person_id}`)} data-testid={`booked-view-${m.id}`}>View Candidate</Button>}
            {m.can_reschedule && <Button size="sm" variant="outline" onClick={() => setRx(m)} data-testid={`booked-reschedule-${m.id}`}>Reschedule</Button>}
            {m.can_cancel && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setCx(m)} data-testid={`booked-cancel-${m.id}`}>Cancel</Button>}
          </div>
        </div>
      ))}
      {rx && <RescheduleDialog m={rx} onClose={() => setRx(null)} onDone={() => { setRx(null); load(); }} />}
      <AlertDialog open={!!cx} onOpenChange={(o) => { if (!o) setCx(null); }}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>The candidate and host will be notified and the host's calendar event removed. The meeting history is kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="booked-cancel-dismiss">Keep</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={cancel} data-testid="booked-cancel-confirm">Cancel appointment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RescheduleDialog({ m, onClose, onDone }) {
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSlots(null); setSlot(null);
    api.get(`/booking/meetings/${m.id}/slots`, { params: { date: isod(date), tz: TZ } })
      .then((r) => setSlots(r.data.slots)).catch((e) => { toast.error(apiError(e)); setSlots([]); });
  }, [m.id, date]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/booking/meetings/${m.id}/reschedule`, { start: slot.start }); toast.success("Appointment rescheduled"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-md" data-testid="booked-reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule · {m.candidate_name}</DialogTitle>
          <DialogDescription>Current: {fmt(m.start)} with {m.host_name}. Showing {m.host_name}'s current availability.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <button onClick={() => setDate(new Date(date.getTime() - 864e5))} className="p-1.5 rounded hover:bg-slate-100" data-testid="rs-prev"><ChevronLeft className="w-4 h-4" /></button>
          <p className="text-sm font-semibold text-slate-700">{date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
          <button onClick={() => setDate(new Date(date.getTime() + 864e5))} className="p-1.5 rounded hover:bg-slate-100" data-testid="rs-next"><ChevronLeft className="w-4 h-4 rotate-180" /></button>
        </div>
        {slots === null ? <p className="text-sm text-slate-400 text-center py-4">Loading times…</p>
          : slots.length === 0 ? <p className="text-sm text-slate-400 text-center py-4" data-testid="rs-no-slots">No open times this day.</p>
          : <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto" data-testid="rs-slot-grid">
              {slots.map((s) => (
                <button key={s.start} onClick={() => setSlot(s)} data-testid={`rs-slot-${s.start}`}
                  className={`rounded-md border py-1.5 text-sm font-medium ${slot?.start === s.start ? "border-brand bg-brand text-white" : "border-slate-200 text-slate-700 hover:border-brand"}`}>{s.label}</button>
              ))}
            </div>}
        <Button className="w-full bg-brand hover:bg-brand-strong" disabled={!slot || busy} onClick={save} data-testid="rs-confirm">{busy ? "Saving…" : "Confirm New Time"}</Button>
      </DialogContent>
    </Dialog>
  );
}
